<template>
  <div style="width: 100%">
    <apexchart
      type="bar"
      :height="height"
      :options="series.chartOptions"
      :series="series.series"
    ></apexchart>
    <v-icon
      v-if="series.loading"
      style="top: -40%; left: 50%; z-index: 999; position: relative"
    >
      mdi-autorenew mdi-spin
    </v-icon>
  </div>
</template>
<script>
import VueApexCharts from 'vue-apexcharts'
import moment from 'moment'

import { isBetweenDates } from '@/utils/date-utils'

const yearDivision = 'year'
const yearTimeGroup = 'YYYY'

const monthDivision = 'month'
const monthTimeGroup = 'YYYY-MM'

const weekDivision = 'isoWeek'
const weekTimeGroup = 'GGGG-WW'

const dayDivision = 'day'
const dayTimeGroup = 'YY-MM-DD'

const dateStringFormat = 'YYYYMMDD'

const dateDivisionLevels = {
  year: 0,
  month: 1,
  week: 2,
  day: 3,
}

// Valor 1 para las Comunicaciones, Valor 2 para las Notificaciones
const typeComunication = '1'
const typeNotification = '2'

const notificationTypeComunication = 'Comunicacion'
const notificationTypeNotification = 'Notificacion'

const receivedState = 'Recibida'
const acceptedState = 'Aceptado'
const expiredState = 'Expirada'
const pendingState = 'Pendiente'

export default {
  components: {
    apexchart: VueApexCharts,
  },
  props: {
    chartname: String,
    title: String,
    notifications: Array,
    height: String,
    dateFrom: String,
    dateTo: String,
  },
  data() {
    var theme = this.$vuetify.theme.dark ? 'dark' : 'light'
    return {
      filteredNotifications: [],
      actualDateFrom: '',
      actualDateTo: '',
      detailDateFrom: '',
      detailDateTo: '',
      timeGroup: yearTimeGroup,
      initialTimeUnit: yearTimeGroup,
      timeUnit: yearDivision,
      chartOptions: {
        chart: {
          zoom: {
            enabled: true,
          },
          stacked: true,
        },
        theme: {
          mode: theme,
        },
        dataLabels: {
          enabled: true,
        },
        stroke: {
          width: 1,
          colors: ['#fff'],
        },
        title: {
          text: this.title,
          align: 'left',
        },
        xaxis: {
          categories: [],
        },
        colors: [
          '#80c7fd',
          '#008FFB',
          '#80f1cb',
          '#00E396',
          '#e3928d',
          '#eb4034',
        ],
        noData: {
          text: this.$t('graphs.loading'),
          align: 'center',
          verticalAlign: 'middle',
          offsetX: 0,
          offsetY: 0,
          style: {
            color: '#000000',
            fontSize: '14px',
            fontFamily: 'Helvetica',
          },
        },
      },
    }
  },

  computed: {
    series() {
      if (this.notifications === undefined || this.notifications.length === 0) {
        return {
          chartOptions: this.chartOptions,
          series: [],
          loading: true,
        }
      }
      // notifications accumulate by state
      let dataMap = {}
      for (let notificationIdx in this.notifications) {
        let notification = this.notifications[notificationIdx]
        let type = this.parseType(notification.tipo_envio)
        if (type === '') {
          console.log('unexpected notification type')
          continue
        }
        // Received
        let dateString = this.getReceivedDateGroup(notification)
        this.updateDataMap(dataMap, dateString, receivedState, type)

        // Accepted or expired
        let state = this.getEstado(notification)
        if (state === acceptedState || state === expiredState) {
          dateString = this.getDateGroup(notification, state, type)
          this.updateDataMap(dataMap, dateString, state, type)
        }
      }

      // Generating series
      let categories = []
      let seriesRC = {
        notificationType: typeComunication,
        group: receivedState,
        name: 'comunicationReceived',
        data: [],
      }
      let seriesAC = {
        notificationType: typeComunication,
        group: acceptedState,
        name: 'comunicationAccepted',
        data: [],
      }
      let seriesEC = {
        notificationType: typeComunication,
        group: expiredState,
        name: 'comunicationExpired',
        data: [],
      }
      let seriesRN = {
        notificationType: typeNotification,
        group: receivedState,
        name: 'notificationReceived',
        data: [],
      }
      let seriesAN = {
        notificationType: typeNotification,
        group: acceptedState,
        name: 'notificationAccepted',
        data: [],
      }
      let seriesEN = {
        notificationType: typeNotification,
        group: expiredState,
        name: 'notificationExpired',
        data: [],
      }

      let xkeys = []

      let dataKeyList = this.generateDataKeyList()
      for (let i = 0; i < dataKeyList.length; i++) {
        let dataKey = dataKeyList[i]
        categories.push(dataKey)
        let values = dataMap[dataKey]
        if (!values) {
          values = {
            Recibida: {
              Comunicacion: 0,
              Notificacion: 0,
            },
            Aceptado: {
              Comunicacion: 0,
              Notificacion: 0,
            },
            Expirada: {
              Comunicacion: 0,
              Notificacion: 0,
            },
          }
        }

        seriesAC.data.push(values.Aceptado.Comunicacion)
        seriesRC.data.push(values.Recibida.Comunicacion)
        seriesEC.data.push(values.Expirada.Comunicacion)

        seriesAN.data.push(values.Aceptado.Notificacion)
        seriesRN.data.push(values.Recibida.Notificacion)
        seriesEN.data.push(values.Expirada.Notificacion)

        xkeys.push(dataKey)
      }

      seriesRC.name = this.$t('notificationsByDate.legend.' + seriesRC.name)
      seriesAC.name = this.$t('notificationsByDate.legend.' + seriesAC.name)
      seriesEC.name = this.$t('notificationsByDate.legend.' + seriesEC.name)
      seriesRN.name = this.$t('notificationsByDate.legend.' + seriesRN.name)
      seriesAN.name = this.$t('notificationsByDate.legend.' + seriesAN.name)
      seriesEN.name = this.$t('notificationsByDate.legend.' + seriesEN.name)

      let series = [seriesRN, seriesRC, seriesAN, seriesAC, seriesEN, seriesEC]

      let pack = {}
      if (this.notifications.length === 0) {
        pack = {
          chartOptions: this.createCustomOptions(xkeys),
          series: [],
          loading: true,
        }
      } else {
        pack = {
          chartOptions: this.createCustomOptions(xkeys),
          series: series,
          loading: false,
        }
      }
      return pack
    },
  },
  methods: {
    createCustomOptions(xkeys) {
      // Building chart options
      return {
        noData: {
          text: this.$t('graphs.loading'),
          align: 'center',
          verticalAlign: 'middle',
          offsetX: 0,
          offsetY: 0,
          style: {
            color: '#000000',
            fontSize: '14px',
            fontFamily: 'Helvetica',
          },
        },
        title: {
          text: this.title + ': ' + this.$t('graphs.timeUnit.' + this.timeUnit),
          align: 'left',
        },
        xaxis: {
          categories: xkeys,
        },
        chart: {
          events: {
            dataPointSelection: (event, chartContext, config) => {
              setTimeout(() => {
                this.zoomNextLevel(
                  xkeys[config.dataPointIndex],
                  this.series.series[config.seriesIndex]
                )
              }, 10)
            },
            xAxisLabelClick: (event, chartContext, config) => {
              setTimeout(() => {
                this.zoomNextLevel(xkeys[config.labelIndex])
              }, 10)
            },
          },
          toolbar: {
            show: true,
            offsetX: 0,
            offsetY: 25,
            tools: {
              download: false,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false,
              customIcons: [
                {
                  icon: '<span class="mdi mdi-undo"/>',
                  index: 0,
                  title: 'Atrás',
                  class: '',
                  click: () => {
                    this.zoomPreviousLevel()
                  },
                },
              ],
            },
          },
        },
        theme: {
          mode: this.$vuetify.theme.dark ? 'dark' : 'light',
        },
      }
    },
    parseType(type) {
      switch (type) {
        case typeComunication:
          return notificationTypeComunication
        case typeNotification:
          return notificationTypeNotification
        default:
          return ''
      }
    },
    updateDataMap(dataMap, dateString, state, type) {
      if (dataMap[dateString] === undefined) {
        dataMap[dateString] = {
          Recibida: {
            Comunicacion: 0,
            Notificacion: 0,
          },
          Aceptado: {
            Comunicacion: 0,
            Notificacion: 0,
          },
          Expirada: {
            Comunicacion: 0,
            Notificacion: 0,
          },
        }
      }
      dataMap[dateString][state][type] += 1
    },
    generateDataKeyList() {
      let currentMoment = moment(this.actualDateFrom)
      let endMoment = moment(this.actualDateTo)
      let keyList = []
      while (currentMoment.isSameOrBefore(endMoment, this.timeUnit)) {
        // console.log(this.timeGroup,':',currentMoment.format(this.timeGroup))
        keyList.push(currentMoment.format(this.timeGroup))
        currentMoment.add(1, this.timeUnit)
      }
      return keyList
    },
    setTimeGroup(newTimeGroup, newTimeUnit) {
      this.timeGroup = newTimeGroup
      this.timeUnit = newTimeUnit
    },
    getDateGroup(notification, state) {
      let dateString
      if (state == acceptedState) {
        dateString = notification.fecha_aceptacion
      } else if (state == expiredState) {
        if (
          notification.tipo_envio === typeComunication &&
          (notification.fecha_expiracion === '' ||
            notification.fecha_expiracion === undefined)
        ) {
          dateString = this.generateComunicationExpiration(
            notification.fecha_puesta_disposicion
          )
        } else {
          dateString = notification.fecha_expiracion
        }
      } else if (state == pendingState) {
        dateString = notification.fecha_puesta_disposicion
      }
      let out = moment(dateString).format(this.timeGroup)
      return out
    },
    generateComunicationExpiration(date) {
      return moment(date).add(10, dayDivision).format(dateStringFormat)
    },
    getReceivedDateGroup(notification) {
      let dateString = notification.fecha_puesta_disposicion
      let out = moment(dateString).format(this.timeGroup)
      return out
    },
    getEstado(notification) {
      return notification.estado
    },
    getGridTitle(serie) {
      let title = this.$t('graphs.notificationsGridTitle')

      if (serie?.notificationType === typeComunication) {
        title = this.$t(
          'notificationsByDate.gridTitle.comunication' + serie.group
        )
      } else if (serie?.notificationType === typeNotification) {
        title = this.$t(
          'notificationsByDate.gridTitle.notification' + serie.group
        )
      }
      return title
    },
    filterNotifications(serie) {
      const { detailDateFrom, detailDateTo, timeGroup, notifications } = this
      const filterByType = serie?.notificationType
      const filterByGroup = serie?.group

      this.filteredNotifications = notifications.filter((notification) => {
        const state = this.getEstado(notification)
        const receivedDateString = this.getReceivedDateGroup(notification)

        // Determina la fecha según el grupo
        const dateString =
          filterByGroup === receivedState
            ? receivedDateString
            : this.getDateGroup(notification, state)

        // Filtra por tipo y grupo si aplica
        const matchesType =
          !filterByType || notification.tipo_envio === filterByType
        const matchesGroup =
          !filterByGroup ||
          filterByGroup === receivedState ||
          filterByGroup === state

        // Filtra por rango de fechas
        const inRange =
          isBetweenDates(dateString, detailDateFrom, detailDateTo, timeGroup) ||
          (!filterByGroup &&
            isBetweenDates(
              receivedDateString,
              detailDateFrom,
              detailDateTo,
              timeGroup
            ))

        return matchesType && matchesGroup && inRange
      })
    },
    zoomNextLevel(selectedX, serie = undefined) {
      if (this.timeUnit === yearDivision) {
        this.zoomMonthsLevel(selectedX)
      } else if (this.timeUnit === monthDivision) {
        this.zoomWeeksLevel(selectedX)
      } else if (this.timeUnit === weekDivision) {
        this.zoomDaysLevel(selectedX)
      } else if (this.timeUnit === dayDivision) {
        let part = selectedX.split('-')
        let prefixYear = '20'
        if (!dayTimeGroup.startsWith('YY-')) {
          prefixYear = ''
        }
        this.detailDateFrom = moment(
          prefixYear + part[0] + part[1] + part[2]
        ).format(dateStringFormat)
        this.detailDateTo = moment(
          prefixYear + part[0] + part[1] + part[2]
        ).format(dateStringFormat)

        this.filterNotifications(serie)
        let title =
          this.getGridTitle(serie) +
          this.$d(
            new Date(moment(this.detailDateFrom).format('YYYY-MM-DD')),
            'i18nDate'
          )
        this.$emit(
          'detail-level',
          this.detailDateFrom,
          title,
          this.filteredNotifications
        )
      }
    },
    zoomPreviousLevel() {
      if (
        dateDivisionLevels[this.initialTimeUnit] >=
        dateDivisionLevels[this.timeUnit]
      ) {
        return
      }
      if (this.timeUnit === monthDivision) {
        this.resetDates()
      } else if (this.timeUnit === weekDivision) {
        let year = moment(this.actualDateFrom).format(yearTimeGroup)
        this.zoomMonthsLevel(year)
      } else if (this.timeUnit === dayDivision) {
        let month = moment(this.actualDateFrom).format(monthTimeGroup)
        this.zoomWeeksLevel(month)
      }
    },
    zoomMonthsLevel(selectedYear) {
      this.actualDateFrom = selectedYear + '0101'
      this.actualDateFrom = this.startDate(this.actualDateFrom, this.dateFrom)
      this.actualDateTo = this.dateOrToday(selectedYear + '1231')
      this.actualDateTo = this.endDate(this.actualDateTo, this.dateTo)
      this.setTimeGroup(monthTimeGroup, monthDivision)
    },
    zoomWeeksLevel(selectedMonth) {
      let parts = selectedMonth.split('-')
      this.actualDateFrom = parts[0] + parts[1] + '01'
      let lastDayOfMonth = moment(this.actualDateFrom)
        .endOf(monthDivision)
        .format(dateStringFormat)
      this.actualDateTo = this.dateOrToday(lastDayOfMonth)
      this.setTimeGroup(weekTimeGroup, weekDivision)
    },
    zoomDaysLevel(selectedWeek) {
      let weekMoment = moment(selectedWeek, weekTimeGroup)
      this.actualDateFrom = weekMoment.format(dateStringFormat)
      let lastDayOfWeek = moment(this.actualDateFrom)
        .add(6, dayDivision)
        .format(dateStringFormat)
      this.actualDateTo = this.dateOrToday(lastDayOfWeek)
      this.setTimeGroup(dayTimeGroup, dayDivision)
    },
    dateOrToday(currentDateTo) {
      if (moment().isBefore(moment(currentDateTo))) {
        return moment().format(dateStringFormat)
      }
      return currentDateTo
    },
    startDate(currentDateFrom, initialDateFrom) {
      if (moment(currentDateFrom).isBefore(moment(initialDateFrom))) {
        return moment(initialDateFrom).format(dateStringFormat)
      }
      return currentDateFrom
    },
    endDate(currentDateTo, initialDateTo) {
      if (moment(currentDateTo).isAfter(moment(initialDateTo))) {
        return moment(initialDateTo).format(dateStringFormat)
      }
      return currentDateTo
    },
    resetDates() {
      this.actualDateFrom = this.dateFrom
      this.actualDateTo = this.dateTo
      this.setTimeGroup(yearTimeGroup, yearDivision)
    },
    setInitialLevel() {
      let rangeDays = moment(this.dateTo).diff(moment(this.dateFrom), 'days')
      //console.log(rangeDays)
      if (rangeDays > 365) {
        this.setTimeGroup(yearTimeGroup, yearDivision)
      } else {
        this.setTimeGroup(monthTimeGroup, monthDivision)
      }
      this.initialTimeUnit = this.timeUnit
    },
    setInitialValues() {
      this.actualDateFrom = this.dateFrom
      this.actualDateTo = this.dateTo
      this.setInitialLevel()
    },
  },
  created() {
    this.setInitialValues()
  },
  watch: {
    dateFrom: {
      deep: true,
      handler: function () {
        this.setInitialValues()
      },
    },
  },
}
</script>
